import React, { CSSProperties, useEffect } from 'react';

import { Box, Typography } from '@mui/material';

import Eyebrow from './Eyebrow';
import { tokens } from '../theme';

// The booking widget from the booking-widget repo (booking.scaledtech.consulting). It checks
// the business Google Calendar, books a Google Meet call and sends the invite. The privacy
// notice describes what it stores, so keep the two in sync if this changes.
export const bookingScriptUrl = 'https://booking.scaledtech.consulting/booking.js';
export const bookingSite = 'scaledtech-google';
export const bookingAnchor = 'book';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'st-booking': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        site: string;
      };
    }
  }
}

const scriptId = 'st-booking-script';

// Widget colours for the navy Stitch booking card; it reads --st-* custom properties from
// its host. Contrast is checked with the real widget in the Contact page preview.
const onNavy = {
  '--st-bg': '#0B2545',
  '--st-text': '#FFFFFF',
  '--st-muted': '#C4D0E3',
  '--st-border': 'rgba(255, 255, 255, 0.16)',
  '--st-border-strong': 'rgba(255, 255, 255, 0.4)',
  '--st-accent': '#4CD6FB',
  '--st-accent-text': '#001026',
  '--st-day-bg': 'rgba(76, 214, 251, 0.12)',
  '--st-day-text': '#FFFFFF',
  '--st-off': '#A9BBD6',
  '--st-hover': 'rgba(255, 255, 255, 0.08)',
  '--st-choice-bg': 'rgba(255, 255, 255, 0.04)',
  '--st-link': '#4CD6FB',
  '--st-focus': '#FFFFFF',
  '--st-input-bg': '#FFFFFF',
  '--st-input-text': '#131B2E',
  '--st-max-width': 'none',
  '--st-radius': '12px',
} as CSSProperties;

function BookingWidget() {
  useEffect(() => {
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = bookingScriptUrl;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  return (
    <Box
      component='section'
      id={bookingAnchor}
      aria-labelledby='book-heading'
      sx={{
        scrollMarginTop: '96px',
        bgcolor: tokens.navy,
        color: '#FFFFFF',
        borderRadius: 3,
        p: { xs: 2.5, md: 3.5 },
        boxShadow: '0 8px 24px -8px rgba(0, 16, 38, 0.35)',
      }}
    >
      <Eyebrow tone='navy'>Introductory call</Eyebrow>
      <Typography
        id='book-heading'
        component='h2'
        variant='h4'
        sx={{ color: '#FFFFFF', mt: 1.5 }}
      >
        Book a 20-minute call
      </Typography>
      <Typography
        variant='bodySm'
        component='p'
        sx={{ color: tokens.onNavyMuted, mt: 1, mb: 2.5 }}
      >
        Pick a time below. You get a Google Calendar invite with a Google Meet link.
      </Typography>
      <st-booking
        site={bookingSite}
        style={onNavy}
      >
        {/* Shown until the widget loads, and if it cannot. */}
        <p>
          To arrange a call, email{' '}
          <a
            href='mailto:info@scaledtech.consulting'
            style={{ color: '#FFFFFF' }}
          >
            info@scaledtech.consulting
          </a>{' '}
          or use the form on this page.
        </p>
      </st-booking>
    </Box>
  );
}

export default BookingWidget;
